import { memo, useState } from 'react'
import type { ObraPendenciaEnriquecida } from '@hooks/useObraPendencias.js'
import type { ObraPendenciaStatus } from '@services/db.js'
import { Badge } from '@components/primitives/Badge.js'
import { Button } from '@components/primitives/Button.js'
import { formatDate } from '@utils/format.js'

const STATUS_LABEL: Record<ObraPendenciaStatus, string> = {
  aberta: 'Aberta',
  'em-andamento': 'Em andamento',
  resolvida: 'Resolvida',
}

const STATUS_VARIANT: Record<ObraPendenciaStatus, 'info' | 'warning' | 'success'> = {
  aberta: 'info',
  'em-andamento': 'warning',
  resolvida: 'success',
}

const PRIORIDADE_LABEL = { baixa: 'Baixa', media: 'Média', alta: 'Alta' } as const
const PRIORIDADE_VARIANT = { baixa: 'info', media: 'warning', alta: 'danger' } as const

const NEXT_STATUS: Record<ObraPendenciaStatus, ObraPendenciaStatus> = {
  aberta: 'em-andamento',
  'em-andamento': 'resolvida',
  resolvida: 'aberta',
}

interface Props {
  item: ObraPendenciaEnriquecida
  onMudarStatus: (id: number, status: ObraPendenciaStatus) => Promise<void>
  onEditar: (item: ObraPendenciaEnriquecida) => void
  onRemover: (id: number) => Promise<void>
}

function metaParts(item: ObraPendenciaEnriquecida): string[] {
  const parts: string[] = []
  if (item.faseLabel) parts.push(item.faseLabel)
  if (item.responsavel) parts.push(item.responsavel)
  if (item.prazo) parts.push(`Prazo ${formatDate(item.prazo)}`)
  if (item.topicoTitulo && item.moduloTitulo) {
    parts.push(`${item.moduloTitulo} · ${item.topicoTitulo}`)
  }
  return parts
}

export const PendenciaCard = memo(function PendenciaCard({
  item,
  onMudarStatus,
  onEditar,
  onRemover,
}: Props) {
  const [confirmando, setConfirmando] = useState(false)
  const meta = metaParts(item)

  const cardClass = [
    'cf3-pend-card',
    `cf3-pend-card--${item.status.replace('-', '')}`,
    item.prioridade === 'alta' && item.status !== 'resolvida'
      ? 'cf3-pend-card--prioridade-alta'
      : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <article className={cardClass}>
      <div className='cf3-pend-card__header'>
        <div className='cf3-pend-card__info'>
          <h3 className='cf3-pend-card__title'>{item.titulo}</h3>
          <div className='cf3-pend-card__badges'>
            <Badge label={STATUS_LABEL[item.status]} variant={STATUS_VARIANT[item.status]} />
            <Badge
              label={PRIORIDADE_LABEL[item.prioridade]}
              variant={PRIORIDADE_VARIANT[item.prioridade]}
            />
          </div>
        </div>
        <div className='cf3-pend-card__actions'>
          <button type='button' className='cf3-pend-card__action' onClick={() => onEditar(item)}>
            Editar
          </button>
          {!confirmando ? (
            <button
              type='button'
              className='cf3-pend-card__action cf3-pend-card__action--danger'
              onClick={() => setConfirmando(true)}
            >
              Excluir
            </button>
          ) : (
            <>
              <button
                type='button'
                className='cf3-pend-card__action cf3-pend-card__action--danger'
                onClick={() => onRemover(item.id)}
              >
                Confirmar
              </button>
              <button
                type='button'
                className='cf3-pend-card__action'
                onClick={() => setConfirmando(false)}
              >
                Cancelar
              </button>
            </>
          )}
        </div>
      </div>

      {item.descricao && <p className='cf3-pend-card__desc'>{item.descricao}</p>}

      {meta.length > 0 && <p className='cf3-pend-card__meta'>{meta.join(' · ')}</p>}

      {item.status !== 'resolvida' && (
        <div className='cf3-pend-card__footer'>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => onMudarStatus(item.id, NEXT_STATUS[item.status])}
          >
            {item.status === 'aberta' ? 'Iniciar' : 'Marcar resolvida'}
          </Button>
          {item.status === 'em-andamento' && (
            <Button variant='ghost' size='sm' onClick={() => onMudarStatus(item.id, 'aberta')}>
              Reabrir
            </Button>
          )}
        </div>
      )}
    </article>
  )
})
